import { useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import type { AirtimeContact } from '@/types/portal';

interface AirtimeFavouritesModalProps {
  open: boolean;
  onClose: () => void;
  contacts: AirtimeContact[];
  onToggle: (id: string) => void;
  onAdd: (name: string, number: string) => void;
}

/**
 * Save a new line and pin or unpin the lines shown in the favourites row on the
 * Buy Airtime screen. Saved lines persist between sessions.
 */
export default function AirtimeFavouritesModal({
  open,
  onClose,
  contacts,
  onToggle,
  onAdd,
}: AirtimeFavouritesModalProps) {
  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [error, setError] = useState('');

  const handleAdd = () => {
    const trimmedName = name.trim();
    const digits = number.replace(/\D/g, '');
    if (!trimmedName) {
      setError('Give this line a name.');
      return;
    }
    if (!/^\d{9,12}$/.test(digits)) {
      setError('Enter a valid phone number (9–12 digits).');
      return;
    }
    if (contacts.some((contact) => contact.number === digits)) {
      setError('That number is already saved.');
      return;
    }
    onAdd(trimmedName, digits);
    setName('');
    setNumber('');
    setError('');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon="ri-star-line"
      title="Favourite lines"
      subtitle="Add a number, then pin the lines you top up most often for one-tap access."
      footer={
        <Button icon="ri-check-line" onClick={onClose}>
          Done
        </Button>
      }
    >
      {/* Add a line */}
      <section className="rounded-lg border border-background-200 bg-background-100 p-4">
        <h3 className="font-heading text-sm font-semibold text-foreground-950">Add a line</h3>
        <p className="mt-0.5 text-xs text-foreground-500">
          Save a number you top up so it is ready next time.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              if (error) setError('');
            }}
            placeholder="Recipient name"
            aria-label="Recipient name"
            className="h-10 w-full rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <input
            value={number}
            inputMode="numeric"
            onChange={(event) => {
              setNumber(event.target.value.replace(/\D/g, ''));
              if (error) setError('');
            }}
            placeholder="Phone number"
            aria-label="Phone number"
            className="h-10 w-full rounded-lg border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
        </div>
        {error ? (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-primary-700">
            <i className="ri-error-warning-line" />
            {error}
          </p>
        ) : null}
        <Button icon="ri-add-line" size="sm" onClick={handleAdd} className="mt-3">
          Add line
        </Button>
      </section>

      {/* Saved lines */}
      <div className="mt-5">
        <h3 className="font-heading text-sm font-semibold text-foreground-950">Saved lines</h3>
        {contacts.length === 0 ? (
          <p className="mt-3 text-sm text-foreground-500">No saved lines yet.</p>
        ) : (
          <ul className="mt-1 divide-y divide-background-100">
            {contacts.map((contact) => {
              const isFavourite = contact.favourite;
              return (
                <li key={contact.id} className="flex items-center gap-3 py-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-sm font-semibold text-accent-800">
                    {contact.name.charAt(0)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-foreground-950">{contact.name}</p>
                    <p className="truncate text-xs text-foreground-500">{contact.number}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onToggle(contact.id)}
                    aria-pressed={isFavourite}
                    aria-label={
                      isFavourite
                        ? `Remove ${contact.name} from favourites`
                        : `Add ${contact.name} to favourites`
                    }
                    className={`flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-base transition-colors ${
                      isFavourite
                        ? 'text-accent-500 hover:bg-accent-50'
                        : 'text-foreground-300 hover:bg-background-200 hover:text-foreground-500'
                    }`}
                  >
                    <i className={isFavourite ? 'ri-star-fill' : 'ri-star-line'} />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Modal>
  );
}